import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnInit } from '@angular/core';
import { AuthService } from "@core/services/auth.service";
import { FilterOption } from "@models/grid/grid-filter";
import { UserInvitationRecord } from '@models/organisations/user-invitation-record';
import { UserRecord } from '@models/organisations/user-record';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UsersAndPermissionsGridService } from "@pages/settings/services/users-and-permissions-grid.service";
import { CommonPageComponent } from "@shared/components/commons/common-page/common-page.component";
import { ConfirmModalComponent } from '@shared/components/commons/confirm/confirm-modal.component';
import {
  AgUsersAndPermissionsActionsComponent
} from "@shared/components/grid/ag-cells/settings/ag-users-and-permissions-actions/ag-users-and-permissions-actions.component";
import {
  GridClientSideComponent
} from "@shared/components/grid/components/grid-client-side/grid-client-side.component";
import { ButtonComponent } from "@shared/controls/button/button.component";
import { HasRoleDirective } from '@shared/directives/has-role.directive';
import { IPartialHeaderData } from "@shared/services/header.service";
import { UserInvitationsApiService } from '@shared/services/user-invitations-api.service';
import { UsersApiService } from '@shared/services/users-api.service';
import { GridApi } from "ag-grid-enterprise";
import { ToastrService } from 'ngx-toastr';
import { catchError, forkJoin, Observable, tap, throwError } from 'rxjs';
import { CreateEditUserModalComponent } from "./components/create-edit-user-modal/create-edit-user-modal.component";

@Component({
  selector: 'app-accounts',
  standalone: true,
  templateUrl: './users-and-permissions-page.component.html',
  styleUrls: ['./users-and-permissions-page.component.scss', '../../styles/settings-styles.scss'],
  imports: [
    ButtonComponent,
    HasRoleDirective,
    GridClientSideComponent
  ],
  providers: [UsersAndPermissionsGridService]
})
export class UsersAndPermissionsPageComponent extends CommonPageComponent implements OnInit {
  private authService = inject(AuthService);
  private usersApiService = inject(UsersApiService);
  private userInvitationsApiService = inject(UserInvitationsApiService);
  private usersAndPermissionsGridService = inject(UsersAndPermissionsGridService);
  private modalService = inject(NgbModal);
  private toastr = inject(ToastrService);

  override readonly headerData: IPartialHeaderData = {
    title: 'Users and Permissions',
    iconClass: 'ic-accounts-20',
    backPath: ['/settings']
  };

  users: (UserRecord | UserInvitationRecord)[] = [];
  checkedUsers: UserRecord[] = [];
  loading = false;
  createEditUserModalOpened = false;

  override ngOnInit(): void {
    super.ngOnInit();
    this.loadUsers().subscribe();
  }

  loadUsers(): Observable<{ users: UserRecord[], invitations: UserInvitationRecord[] }> {
    this.loading = true;

    return forkJoin({
      users: this.usersApiService.getUsers(),
      invitations: this.userInvitationsApiService.getUserInvitations()
    })
      .pipe(
        tap(({ users, invitations }) => {
          this.users = [...invitations, ...users];

          this.setIndividualFilterOptions(users);
          this.setGridFilterOptions();
          this.setGridConfig();
          this.loading = false;
        })
      );
  }

  openCreateEditUserModal(user: UserRecord | null = null): void {
    if (this.createEditUserModalOpened) return;

    this.createEditUserModalOpened = true;

    const modalRef = this.modalService.open(CreateEditUserModalComponent, { size: 'lg' });
    const instance = modalRef.componentInstance as CreateEditUserModalComponent;

    if (user) {
      instance.user = new UserRecord(structuredClone(user));
    } else {
      instance.usersEmails = this.users.map((user) => user.email);
    }

    modalRef.result.then(
      () => {
        this.loadUsers().subscribe();
        this.createEditUserModalOpened = false;
      },
      () => this.createEditUserModalOpened = false
    );
  }

  openDeleteUserModal(data: UserRecord | UserInvitationRecord): void {
    if ('userInvitationId' in data) {
      this.openConfirmDeleteInvitationModal(data as UserInvitationRecord);
    } else {
      this.openConfirmDeleteUserModal(data as UserRecord);
    }
  }

  openConfirmDeleteInvitationModal(invitation: UserInvitationRecord): void {
    const modalRef = this.modalService.open(ConfirmModalComponent);
    const instance = modalRef.componentInstance as ConfirmModalComponent;

    instance.title = 'Delete invitation';
    instance.message = 'User will not be able to join using invitation link. Are you sure you want to proceed?';
    instance.icon = 'ic-trash-20';
    instance.confirmText = 'Yes';
    instance.closeText = 'No';

    instance.confirm = () => this.userInvitationsApiService.deleteInvitation(invitation.userInvitationId)
      .pipe(catchError((error: HttpErrorResponse) => {
        this.toastr.error("Error while deleting user", "Error");
        return throwError(() => error);
      }));

    modalRef.result.then(
      () => {
        this.toastr.success('Invitation deleted', 'Success');
        this.loadUsers().subscribe();
      },
      () => {
      }
    );
  }

  openConfirmDeleteUserModal(user: UserRecord): void {
    const modalRef = this.modalService.open(ConfirmModalComponent);
    const instance = modalRef.componentInstance as ConfirmModalComponent;

    instance.title = 'Delete account';
    instance.message = 'Deleting the user will block the user\'s access to the portal. Are you sure you want to proceed?';
    instance.icon = 'ic-trash-20';
    instance.confirmText = 'Yes';
    instance.closeText = 'No';

    instance.confirm = () => this.usersApiService.deleteUser(user.externalId)
      .pipe(catchError((error: HttpErrorResponse) => {
        this.toastr.error("Error while deleting user", "Error");
        return throwError(() => error);
      }));

    modalRef.result.then(
      () => {
        this.toastr.success('Account deleted', 'Success');

        if (user.externalId === this.authService.currentUserProfile()?.externalUserId) {
          this.authService.logOut();
          return;
        }

        this.loadUsers().subscribe();
      },
      () => {
      }
    );
  }

  openConfirmResetMFAModal(user: UserRecord): void {
    const modalRef = this.modalService.open(ConfirmModalComponent);
    const instance = modalRef.componentInstance as ConfirmModalComponent;

    instance.title = 'Reset MFA';
    instance.message = 'An email will be sent to the user for MFA reset. Do you want to proceed?';
    instance.icon = 'ic-lock-20';
    instance.confirmText = 'Yes';
    instance.closeText = 'No';

    // TODO: implement the request calling when BE done with it

    modalRef.result.then(
      () => {
        // this.toastr.success('MFA reset', 'Success');
        this.loadUsers().subscribe();
      },
      () => {
      }
    );
  }

  openConfirmResetPasswordModal(user: UserRecord): void {
    const modalRef = this.modalService.open(ConfirmModalComponent);
    const instance = modalRef.componentInstance as ConfirmModalComponent;

    instance.title = 'Reset Password';
    instance.message = 'An email will be sent to the user to reset the login password. Do you want to proceed?';
    instance.icon = 'ic-lock-20';
    instance.confirmText = 'Yes';
    instance.closeText = 'No';

    instance.confirm =
      () => this.usersApiService.triggerPasswordReset(user.externalId)
        .pipe(catchError((error: HttpErrorResponse) => {
          this.toastr.error('Error while initiating password reset!', 'Error');
          return throwError(() => error);
        }));

    modalRef.result.then(
      () => {
        this.toastr.success('Password reset', 'Success');
        this.loadUsers().subscribe();
      },
      () => {
      }
    );
  }

  setIndividualFilterOptions(users: UserRecord[]): void {
    // Permission (Admin, Manager, Preparer, User)
    // Status
    // Date Created
    // Last Login
    // 2FA Method

    // TODO: uncomment when UserRecord model updated with dateCreated and lastLogin props
    // this.dateCreatedFilterOptions = users.map(user => {
    //   return {
    //     label: user.dateCreated ? formatDate(user.dateCreated ?? '', 'dd MMM yyyy', 'en') : '',
    //     value: user.dateCreated ?? '',
    //     active: false
    //   };
    // });
    // this.twoFaFilterOptions = [];
    // this.lastLoginFilterOptions = users.map(user => {
    //   return {
    //     label: user.lastLogin ? formatDate(user.lastLogin ?? '', 'dd MMM yyyy', 'en') : '',
    //     value: user.lastLogin ?? '',
    //     active: false
    //   };
    // });
  }

  // ===== Grid  =====

  colDefs = this.usersAndPermissionsGridService.colDefs;

  dateCreatedFilterOptions: FilterOption[] = [];
  twoFaFilterOptions: FilterOption[] = [];
  lastLoginFilterOptions: FilterOption[] = [];

  onGridReady(gridApi: GridApi): void {
    this.usersAndPermissionsGridService.setGridApi(gridApi);
  }

  selectUsers(users: UserRecord[]): void {
    this.checkedUsers = users;
  }

  setGridFilterOptions(): void {
    this.usersAndPermissionsGridService.setFiltersOptions(
      this.dateCreatedFilterOptions,
      this.twoFaFilterOptions,
      this.lastLoginFilterOptions
    );
  }

  setGridConfig(): void {
    this.usersAndPermissionsGridService.setColDefs({
      width: 150,
      minWidth: 150,
      sortable: false,
      cellRenderer: AgUsersAndPermissionsActionsComponent,
      cellClass: 'actions-button-cell',
      cellRendererParams: {
        edit: this.openCreateEditUserModal.bind(this),
        delete: this.openDeleteUserModal.bind(this),
        resetMFA: this.openConfirmResetMFAModal.bind(this),
        resetPassword: this.openConfirmResetPasswordModal.bind(this)
      },
    });
  }
}
